import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import type { IApplicationState } from '@bridebook/web/lib/types';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { getWeddingL10n, getUserL10n } from '@bridebook/web/lib/i18n/selectors';
import type { Extras } from '@sentry/core';
import { Users } from '@bridebook/models';
import { getCurrentUserId } from '@bridebook/web/lib/users/selectors';
import { countriesWithBlockedExternalWeddings } from '@bridebook/toolbox/src/i18n/features';
import { ILeadSource } from '@bridebook/models/source/models/Suppliers/Leads.types';

const SPAM_REGEX_PATTERNS = [
  /\b(?:buesdenew)\b/i,
  /\b(?:cyluee1990)\b/i,
  /\b(?:newweep223)\b/i,
  /\b(?:ultra-high-net-worth)\b/i,
  /\b(?:matterport-technologie)\b/i,
  /\bMy(?: account on)? Insta(?:gram)?(?: account)?:/i,
  // Account-threat phishing wording
  /\b(?:account|store|listing|profile|page) (?:is |has been |was )?(?:suspended|blocked|restricted|offline|deactivated|paused|frozen)\b/i,
  /\bbalances? (?:are |is |have been |has been )?(?:reserved|frozen|held|withheld|blocked)\b/i,
  /\bsecure (?:gate|portal|verification|checkpoint)\b/i,
  /\bverify your (?:account|identity|data|details|information)\b/i,
];

/**
 * !Note: It's important to distinguish email addresses that contain a domain name from standalone URLs.
 */
const URL_REGEX =
  /(?<!\w)(?<!@[\w.+-]*)(?:(?:https?:[/][/]|www[.])\S+|[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:[.][a-z]{2,}){1,2}(?![\w.+-]*@)(?:[/]\S*)?)/i;

// Invisible characters used to break up domains
const INVISIBLE_CHARS_REGEX = /[\u200B-\u200D\u2060\uFEFF\u00AD]/g;

// Characters that render like a dot
const DOT_LOOKALIKES_REGEX = /[‧・･｡。·]/g;

// Cyrillic/Greek letters that look identical to Latin ones
const CONFUSABLES: Record<string, string> = {
  а: 'a',
  с: 'c',
  ԁ: 'd',
  е: 'e',
  ё: 'e',
  һ: 'h',
  і: 'i',
  ј: 'j',
  о: 'o',
  р: 'p',
  ѕ: 's',
  у: 'y',
  х: 'x',
  ԝ: 'w',
  α: 'a',
  ε: 'e',
  ι: 'i',
  ν: 'v',
  ο: 'o',
  ρ: 'p',
  τ: 't',
  υ: 'u',
};
const CONFUSABLES_REGEX = new RegExp(`[${Object.keys(CONFUSABLES).join('')}]`, 'g');

/**
 * Undo common evasion tricks before matching: converts to unicode
 * compatibility form, strips invisible characters, unifies dot look-alikes
 * and maps letters that only look Latin back to Latin.
 */
const normalizeMessage = (text: string) =>
  text
    .normalize('NFKC')
    .toLowerCase()
    .replace(INVISIBLE_CHARS_REGEX, '')
    .replace(DOT_LOOKALIKES_REGEX, '.')
    .replace(CONFUSABLES_REGEX, (char) => CONFUSABLES[char]);

// TLDs accepted when the dot is disguised. Excludes plain English words
// ("to", "me", "live"...) so ordinary prose is not flagged.
const OBFUSCATED_URL_TLDS = 'com|net|org|co|uk|de|fr|ie|io|ly|ee|gy|gd|cc|xyz';

// Domains with a disguised dot, e.g. "tr(.)ee", "bridebook dot com", "tr . ee"
const OBFUSCATED_URL_REGEX = new RegExp(
  `\\b[a-z0-9-]+\\s*(?:[([{]\\s*(?:dot|\\.)\\s*[)\\]}]|\\s(?:dot|\\.)\\s)\\s*(?:${OBFUSCATED_URL_TLDS})\\b`,
  'i',
);

// The userinfo trick: "bridebook.com@tr.ee/xyz" actually opens tr.ee.
// Flags userinfo ending in an unambiguous TLD (real email local-parts
// almost never end in ".com"), or an @-host followed by a path — plain
// emails match neither.
const DECEPTIVE_URL_REGEX = /\.(?:com|net|org)@\S+|@\S+\.[a-z]{2,}\/\S+/i;

// Internal checks below expect already normalized text
const containsUrls = (normalized: string) =>
  URL_REGEX.test(normalized) || OBFUSCATED_URL_REGEX.test(normalized);

const containsDeceptiveUrls = (normalized: string) => DECEPTIVE_URL_REGEX.test(normalized);

const matchesSpamPatterns = (normalized: string) =>
  SPAM_REGEX_PATTERNS.some((pattern) => pattern.test(normalized));

/**
 * Check whether message contains urls, including obfuscated ones
 */
export const messageContainsUrls = (text: string) => containsUrls(normalizeMessage(text || ''));

/**
 * Check whether message contains a url crafted to look like it leads elsewhere
 */
export const messageContainsDeceptiveUrls = (text: string) =>
  containsDeceptiveUrls(normalizeMessage(text || ''));

/**
 * Check whether message contains deny-listed words or phishing wording
 */
export const isSpamMessage = (message: string) => matchesSpamPatterns(normalizeMessage(message));

/**
 * Verify viewer country against the blacklist
 */
const verifyViewerCountry = (weddingCountry: CountryCodes, viewerCountry?: CountryCodes) => {
  if (
    // Check if international weddings are allowed for the viewer country
    viewerCountry &&
    weddingCountry !== viewerCountry &&
    countriesWithBlockedExternalWeddings.includes(viewerCountry)
  ) {
    return false;
  }
  return true;
};

type EnquirySpamCheckArgs = {
  message?: string;
  weddingCountry: CountryCodes;
  viewerCountry?: CountryCodes;
  source?: ILeadSource;
};

export type EnquirySpamCheckExtra =
  | { message: string; reason: 'url' | 'deceptive_url' }
  | { message: string }
  | { weddingCountry: CountryCodes; viewerCountry?: CountryCodes };

export type EnquirySpamCheckResult =
  | { isSpam: false }
  | { isSpam: true; extra: EnquirySpamCheckExtra };

/**
 * Pure spam rules — no Redux, no Sentry. Use on the server (API routes, guards)
 * and anywhere you already have weddingCountry / viewerCountry in hand.
 */
export const verifyEnquirySpamCheck = ({
  message,
  weddingCountry,
  viewerCountry,
  source,
}: EnquirySpamCheckArgs): EnquirySpamCheckResult => {
  const normalized = message ? normalizeMessage(message) : '';

  if (message && containsDeceptiveUrls(normalized)) {
    return { isSpam: true, extra: { message, reason: 'deceptive_url' } };
  }

  if (source !== ILeadSource.Email && message && containsUrls(normalized)) {
    return { isSpam: true, extra: { message, reason: 'url' } };
  }

  if (message && matchesSpamPatterns(normalized)) {
    return { isSpam: true, extra: { message } };
  }

  if (!verifyViewerCountry(weddingCountry, viewerCountry)) {
    return { isSpam: true, extra: { weddingCountry, viewerCountry } };
  }

  return { isSpam: false };
};

/**
 * Client-side spam guard for Redux flows. Wraps {@link verifyEnquirySpamCheck}
 * with state selectors + Sentry. Returns false on spam (caller fakes success).
 * Server routes should call verifyEnquirySpamCheck directly instead.
 */
export const verifySpamCheck = (state: IApplicationState, message?: string) => {
  const reportToSentry = (extra: Extras) =>
    SentryMinimal().captureMessage(`[ENQUIRY_SPAM_DETECTED]`, { extra });

  const result = verifyEnquirySpamCheck({
    message,
    weddingCountry: getWeddingL10n(state).country,
    viewerCountry: getUserL10n(state)?.viewerCountry,
  });

  if (result.isSpam) {
    reportToSentry(result.extra);
    return false;
  }

  return true;
};

/**
 * Blocks account for suspicious activity, e.g. spam messages.
 * Shows overlay blocking the entire app.
 */
export const blockAccount = async (state: IApplicationState) => {
  const userId = getCurrentUserId(state);
  if (!userId) return;
  return Users._.getById(userId).block();
};
